import { useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { IOptionDeal } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IDeal';
import { IMarketArticlesFilterPayload } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import { useArticleDealsList } from '@/creditflowresearch/application/hooks/apiHooks/useArticleDealsList';
import { CFR_ARTICLES } from '@/creditflowresearch/ui/components/creditFlow/articles/FiltersButton/constants';
import { useFiltersContext } from '@framework/ui/Organisms/Filters/FiltersContext';
import { initialArticlesFilterState } from '@/creditflowresearch/application/ArticlesAndStats/Articles/constants';
import { IRegion } from '@/creditflowresearch/domain/vo/Filters/IRegion';
import { getRegionsByCountries } from '@/creditflowresearch/ui/components/creditFlow/articles/helpers';

interface IProps {
  regionList: IRegion[],
}

const useDealSelect = ({ regionList }: IProps) => {
  const [selected, setSelected] = useState<IOptionDeal[]>([]);
  const [currentOptions, setCurrentOptions] = useState<IOptionDeal[]>([]);
  const { getDealsList } = useArticleDealsList();
  const { filters: filtersState } = useFiltersContext();
  const filters: IMarketArticlesFilterPayload = isEmpty(filtersState[CFR_ARTICLES])
    ? initialArticlesFilterState
    : filtersState[CFR_ARTICLES];

  const regionIds = useMemo(
    () => getRegionsByCountries(regionList, filters.includedCountryIds),
    [JSON.stringify(regionList), JSON.stringify(filters.includedCountryIds)],
  );
  const productIds = filters.includedProductIds;

  const initDeals = async (includedDealIds: string[]) => {
    if (isEmpty(includedDealIds)) {
      return;
    }

    const payload = {
      search: null,
      filter: {
        includedProductIds: productIds,
        includedRegionIds: regionIds,
        includedDealIds,
      },
    };

    const deals = await getDealsList(payload);
    const data = deals.map(({ id, name, ...others }) => ({ value: id, label: name, ...others }));

    setCurrentOptions(data);
    setSelected(data);
  };

  const loadDealsList = useCallback(
    async (search: string, callback: (options: IOptionDeal[]) => void) => {
      const payload = {
        search,
        filter: {
          includedProductIds: productIds,
          includedRegionIds: regionIds,
        },
      };

      const deals = await getDealsList(payload);
      const data = deals.map(({ id, name, ...other }) => ({ value: id, label: name, ...other }));

      setCurrentOptions(data);
      callback(data);
      return data;
    },
    [productIds, regionIds],
  );

  const includedDealIds = filters.includedDealIds ?? [];

  useEffect(() => {
    if (includedDealIds.length === 0 && selected.length > 0) {
      setSelected([]);
    }
  }, [includedDealIds.length]);

  return {
    selected,
    setSelected,
    currentOptions,
    loadDealsList,
    initDeals,
  };
};

export default useDealSelect;
